import { inject, Injectable } from '@angular/core'
import { Functions, httpsCallable } from '@angular/fire/functions'
import { PixelService } from '@bloomscorp/ngx-pixel'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Observable, switchMap } from 'rxjs'
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators'
import { OrderUtils } from '../../../../../../commons/utils/order.utils'
import { Order } from '../../../../../../domain/order.model'
import { OrderUtilsService } from '../../../../shared-business-logic/order/order-utils.service'
import { ShopSettingsService } from '../../../auth/shop-settings.service'
import { toSignal } from '@angular/core/rxjs-interop'
import { Stripe } from '@stripe/stripe-js'
import { assertNonNullish } from '../../../../../../commons/assertions'

@Injectable({
	providedIn: 'root',
})
export class StripeService {
	private readonly fireFunctions = inject(Functions)
	private readonly shopSettingsService = inject(ShopSettingsService)
	private readonly orderUtilsService = inject(OrderUtilsService)
	private readonly pixel = inject(PixelService)

	private readonly stripeApiKey$: Observable<string> = this.shopSettingsService.shopSettings$.pipe(
		map((settings) => settings.stripeApiKey),
		distinctUntilChanged(),
	)
	private readonly stripe$: Observable<Stripe | null> = this.stripeApiKey$.pipe(
		switchMap(async (stripeApiKey) => (stripeApiKey ? await loadStripe(stripeApiKey) : null)),
		shareReplay(1),
	)
	private readonly stripe = toSignal(this.stripe$)

	async checkout(order: Order): Promise<{ id: string } & object> {
		const shopSettings = this.shopSettingsService.shopSettings()
		assertNonNullish(shopSettings, 'Shop settings are null or undefined')

		const stripeCheckoutFn = httpsCallable<{ shopUid: string; order: Order }, any>(this.fireFunctions, 'stripeCheckout')
		const stripeSession = await stripeCheckoutFn({ shopUid: shopSettings.uid, order })

		try {
			await this.trackCheckout(order)
		} catch (error) {
			console.error('Error tracking checkout:', error)
		}

		return stripeSession.data
	}

	async redirectToCheckout(sessionId: string): Promise<never | { error: any }> {
		const stripe = this.stripe()
		if (stripe) {
			return stripe.redirectToCheckout({ sessionId })
		}
		return Promise.resolve({ error: "Stripe wasn't loaded" })
	}

	private async trackCheckout(order: Order): Promise<void> {
		const summary = OrderUtils.toOrderPixelSummary(order)
		const translatedSummary = await this.orderUtilsService.translateOrderPixelSummary(summary)
		const pixelEventProperties = this.orderUtilsService.mapToPixelEventProperties(translatedSummary)
		this.pixel.track('InitiateCheckout', pixelEventProperties)
	}
}
